import { ICheckoutPageSettings } from '../../../../api/checkouts/types/checkout-page.type';

/**
 * Depois do pagamento aprovado, levar o comprador para a URL do lojista
 * (`settings.confirmation.successRedirect`), no modelo do `success_url` do
 * Stripe Checkout.
 *
 * A URL vem PRONTA do pay (`successRedirectUrl`), com `{CHECKOUT_SESSION_ID}`
 * já trocado e só quando a sessão está paga — o checkout nunca monta a URL.
 * Do builder vêm só o modo (botão ou automático) e o texto do botão.
 *
 * Entre o submit e a tela de pagamento aprovado os dados viajam por
 * sessionStorage, por referência da assinatura.
 */
export const SUCCESS_REDIRECT_STORAGE_PREFIX =
	'curseduca:checkout-success-redirect:';

export const DEFAULT_SUCCESS_BUTTON_LABEL = 'Continuar';

/** Modo automático: tempo para o comprador ler "Pagamento aprovado!". */
export const AUTO_REDIRECT_DELAY_MS = 2000;

export type SuccessRedirectMode = 'button' | 'auto';

export type SuccessRedirectOptions = {
	mode: SuccessRedirectMode;
	buttonLabel: string;
};

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

/** Só https (ou http em localhost, para o lojista testar). O resto é ignorado. */
export const safeRedirectUrl = (url?: string | null): string | null => {
	if (!url || typeof url !== 'string') return null;
	try {
		const parsed = new URL(url);
		if (parsed.protocol === 'https:') return parsed.toString();
		if (
			parsed.protocol === 'http:' &&
			LOCAL_HOSTS.includes(parsed.hostname)
		)
			return parsed.toString();
		return null;
	} catch {
		return null;
	}
};

/** Opções do builder. `null` = checkout sem redirecionamento configurado. */
export const getSuccessRedirectOptions = (
	settings?: ICheckoutPageSettings | null
): SuccessRedirectOptions | null => {
	const config = settings?.confirmation?.successRedirect;
	if (!config?.url) return null;
	return {
		mode: config.mode === 'auto' ? 'auto' : 'button',
		buttonLabel: config.buttonLabel?.trim() || DEFAULT_SUCCESS_BUTTON_LABEL
	};
};

export type SuccessRedirectStep =
	/** URL na mão: tela de pagamento aprovado com o botão (ou automático). */
	| 'redirect'
	/** Checkout com redirecionamento, aprovação ainda não chegou: tela de espera. */
	| 'wait'
	/** Fluxo de sempre. */
	| null;

/**
 * Próximo passo de uma ASSINATURA depois do submit (recusa e PENDING já
 * pararam antes). Pix segue o caminho próprio dele (tela do QR Code), que
 * também leva ao destino quando o pagamento confirmar.
 */
export const resolveSuccessRedirectStep = ({
	successRedirectUrl,
	isPixSubscription,
	enabled,
	firstChargeStatus
}: {
	successRedirectUrl?: string | null;
	isPixSubscription: boolean;
	enabled: boolean;
	firstChargeStatus?: string;
}): SuccessRedirectStep => {
	if (safeRedirectUrl(successRedirectUrl)) return 'redirect';
	if (isPixSubscription || !enabled) return null;
	// Motor do gateway (sem `firstCharge`): a aprovação do cartão chega pelo
	// webhook, segundos depois — a tela de espera consulta até ela chegar.
	// Com `firstCharge` o desfecho já veio: sem URL é o fluxo de sempre
	// (ex.: TRIAL não tem cobrança aprovada).
	return firstChargeStatus ? null : 'wait';
};

type StoredSuccessRedirect = Partial<SuccessRedirectOptions> & {
	url?: string | null;
};

export const storeSuccessRedirect = (
	reference: string,
	value: StoredSuccessRedirect
) => {
	try {
		sessionStorage.setItem(
			`${SUCCESS_REDIRECT_STORAGE_PREFIX}${reference}`,
			JSON.stringify(value)
		);
	} catch {
		// Sem storage, a tela de espera consulta o pay e recebe a URL de novo
		// (com o botão padrão).
	}
};

export const readStoredSuccessRedirect = (
	reference: string
): { url: string | null } & SuccessRedirectOptions => {
	let stored: StoredSuccessRedirect = {};
	try {
		stored = JSON.parse(
			sessionStorage.getItem(
				`${SUCCESS_REDIRECT_STORAGE_PREFIX}${reference}`
			) || '{}'
		);
	} catch {
		stored = {};
	}
	return {
		url: safeRedirectUrl(stored.url),
		mode: stored.mode === 'auto' ? 'auto' : 'button',
		buttonLabel:
			(typeof stored.buttonLabel === 'string' &&
				stored.buttonLabel.trim()) ||
			DEFAULT_SUCCESS_BUTTON_LABEL
	};
};

/**
 * Navega a janela de CIMA (o checkout pode estar num iframe). Se o navegador
 * recusar, o botão continua na tela.
 */
export const navigateTop = (url: string) => {
	try {
		(window.top ?? window).location.assign(url);
	} catch {
		window.location.assign(url);
	}
};
